/**
 * Toast provider – renders app-wide toasts in a fixed container; same position on all screen types.
 */

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Toast, type ToastVariant } from '@components/common/Toast';
import { ToastContext, type ToastContextValue } from './ToastContext';

const AUTO_DISMISS_MS = 5000;

interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  iconType?: import('@design-tokens/icons').IconType;
}

let toastId = 0;

function nextId(): string {
  return `toast-${++toastId}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const t = timeoutsRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timeoutsRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (variant: ToastVariant, message: string, options?: { iconType?: import('@design-tokens/icons').IconType }) => {
      const id = nextId();
      setToasts((prev) => [{ id, variant, message, iconType: options?.iconType }, ...prev]);
      const t = setTimeout(() => {
        removeToast(id);
      }, AUTO_DISMISS_MS);
      timeoutsRef.current.set(id, t);
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, options?: { iconType?: import('@design-tokens/icons').IconType }) =>
      addToast('success', message, options),
    [addToast]
  );
  const error = useCallback(
    (message: string, options?: { iconType?: import('@design-tokens/icons').IconType }) =>
      addToast('error', message, options),
    [addToast]
  );
  const warning = useCallback(
    (message: string, options?: { iconType?: import('@design-tokens/icons').IconType }) =>
      addToast('warning', message, options),
    [addToast]
  );
  const info = useCallback(
    (message: string, options?: { iconType?: import('@design-tokens/icons').IconType }) =>
      addToast('info', message, options),
    [addToast]
  );

  const value = useMemo<ToastContextValue>(
    () => ({ success, error, warning, info }),
    [success, error, warning, info]
  );

  return (
    <ToastContext value={value}>
      {children}
      <div className="bhg-toast-container" aria-label="Notifications">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            message={t.message}
            iconType={t.iconType}
            onDismiss={() => removeToast(t.id)}
            dismissible
          />
        ))}
      </div>
    </ToastContext>
  );
}
